import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  ErrorState,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  type LocalDataColumn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBadge,
  type Tone,
} from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Mail, RotateCw } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SettingsPage } from "@/components/layout/SettingsPage";
import {
  type EmailTemplate,
  type OutboxRow,
  useEmailTemplate,
  useRecentEmails,
  useSendTestEmail,
} from "@/hooks/use-email";
import { useSession } from "@/lib/auth-client";
import { EditTemplatePage } from "./EditTemplatePage";
import { TemplatesCard } from "./TemplatesCard";

const TEMPLATE_OPTIONS: { value: EmailTemplate; label: string }[] = [
  { value: "test", label: "Test (pipeline check)" },
  { value: "welcome", label: "Welcome" },
  { value: "password-reset", label: "Password reset" },
  { value: "notification", label: "Notification" },
];

const sendTestSchema = z.object({
  to: z.string().email("Enter a valid email address"),
  template: z.enum(["test", "welcome", "password-reset", "notification"]),
});

type SendTestValues = z.infer<typeof sendTestSchema>;

const STATUS_TONES: Record<OutboxRow["status"], Tone> = {
  pending: "neutral",
  queued: "info",
  sending: "info",
  sent: "warning",
  delivered: "success",
  bounced: "danger",
  complained: "danger",
  failed: "danger",
};

const STATUS_OPTIONS = Object.keys(STATUS_TONES).map((status) => ({
  value: status,
  label: status,
}));

const RECENT_COLUMNS: LocalDataColumn<OutboxRow>[] = [
  {
    key: "to",
    label: "To",
    type: "text",
    accessor: (row) => row.toAddresses.join(", "),
    render: (row) => <span className="text-muted-foreground">{row.toAddresses.join(", ")}</span>,
  },
  {
    key: "subject",
    label: "Subject",
    type: "text",
    accessor: (row) => row.subject,
    render: (row) => <span className="font-medium">{row.subject}</span>,
  },
  {
    key: "template",
    label: "Template",
    type: "text",
    accessor: (row) => row.templateName,
    render: (row) => <span className="text-muted-foreground">{row.templateName ?? "—"}</span>,
  },
  {
    key: "status",
    label: "Status",
    type: "enum",
    options: STATUS_OPTIONS,
    accessor: (row) => row.status,
    render: (row) => <StatusBadge status={row.status} tones={STATUS_TONES} />,
  },
  { key: "attempts", label: "Attempts", type: "number", accessor: (row) => row.attempts },
  {
    key: "created",
    label: "Created",
    type: "date",
    accessor: (row) => row.createdAt.slice(0, 10),
    render: (row) => (
      <span className="text-muted-foreground">{new Date(row.createdAt).toLocaleString()}</span>
    ),
  },
];

function TemplatePage({ templateKey, onClose }: { templateKey: string; onClose: () => void }) {
  const detail = useEmailTemplate(templateKey);
  if (detail.isError) {
    return (
      <ErrorState
        message={detail.error.message || "The template didn't load"}
        onRetry={() => void detail.refetch()}
      />
    );
  }
  if (!detail.data) return <Skeleton className="h-96 w-full" />;
  return <EditTemplatePage key={templateKey} template={detail.data} onClose={onClose} />;
}

export function EmailSettingsTab() {
  const { template } = useSearch({ from: "/dashboard/settings/email" });
  const navigate = useNavigate({ from: "/dashboard/settings/email" });
  if (template) {
    return (
      <TemplatePage
        templateKey={template}
        onClose={() => void navigate({ search: {}, replace: true })}
      />
    );
  }
  return <EmailSettings onEdit={(key) => void navigate({ search: { template: key } })} />;
}

function EmailSettings({ onEdit }: { onEdit: (key: string) => void }) {
  const { data: session } = useSession();
  const sendTest = useSendTestEmail();
  const recent = useRecentEmails();

  const form = useForm<SendTestValues>({
    resolver: zodResolver(sendTestSchema),
    defaultValues: { to: session?.user?.email ?? "", template: "test" },
  });

  function onSubmit(values: SendTestValues) {
    sendTest.mutate(values);
  }

  return (
    <SettingsPage>
      <div className="space-y-6">
        <TemplatesCard onEdit={onEdit} />

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-4 w-4 text-primary" />
              Send a test email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="grid gap-4 sm:grid-cols-[1fr_220px_auto] sm:items-end"
              >
                <FormField
                  control={form.control}
                  name="to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recipient</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="you@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Try <code>success@simulator.amazonses.com</code> to exercise the full
                        pipeline.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="template"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full" aria-label="Template">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TEMPLATE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={sendTest.isPending}>
                  {sendTest.isPending ? "Sending…" : "Send"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <DataTable<OutboxRow>
          rows={recent.data ?? []}
          loading={recent.isLoading}
          error={
            recent.isError ? (recent.error?.message ?? "Failed to load recent emails") : undefined
          }
          onRetry={() => recent.refetch()}
          rowKey={(row) => row.id}
          label="Recent emails"
          title="Recent emails"
          scope="Recent emails"
          columns={RECENT_COLUMNS}
          empty={{ title: "No emails yet", icon: Mail }}
          controls={
            <Button
              variant="outline"
              size="sm"
              onClick={() => recent.refetch()}
              disabled={recent.isFetching}
            >
              <RotateCw className={`h-4 w-4 ${recent.isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          }
        />
      </div>
    </SettingsPage>
  );
}
